/* eslint-disable react/prop-types */
import * as React from 'react';

import { translate } from '~l10n/client';

import Constants from '../../../../../shared/Constants';
import Input from '../../../../shared/components/elements/Input';
import Select from '../../../../shared/components/elements/Select';
import AmexIcon from '../../../../shared/icons/AmexIcon';
import GenericCardIcon from '../../../../shared/icons/GenericCardIcon';
import MasterCardIcon from '../../../../shared/icons/MasterCardIcon';
import SuccessIconLarge from '../../../../shared/icons/SuccessIconLarge';
import VisaIcon from '../../../../shared/icons/VisaIcon';
import { LicensePurchase } from './LicensePurchase';

export default function LicenseView(props: {
  controller: any;
  model: any;
  registration: any;
}) {
  const controller = props.controller;
  const model = props.model;
  const license = model.licenseModel;

  const showDiscountRow = () => {
    if (license.percentageDiscount > 0) {
      return (
        <div key="children-div-9" className="order-summary-row">
          <span className="license-receipt-left-column">
            {translate('Group Purchase Discount')}
          </span>
          <span className="order-summary-right-column">
            {license.percentageDiscount}%
          </span>
        </div>
      );
    } else if (license.couponSavings) {
      return (
        <div key="children-div-9" className="order-summary-row">
          <span className="license-receipt-left-column">
            {translate('Coupon Discount Per Seat')}
          </span>
          <span className="order-summary-right-column">
            {license.couponSavings}
          </span>
        </div>
      );
    } else {
      return <div></div>;
    }
  };

  let children = [];

  if (license.licenseStep === Constants.LICENSE_STEP) {
    children = [
      <div key="children-div-1" className="activate-link-container">
        <span className="link" onClick={controller.enterLicenseKeyClicked}>
          {translate('Enter License Key')}
        </span>
      </div>,
      <div
        key="children-div-2"
        className="plan-type-container"
        style={{ marginBottom: '1em' }}
      >
        <span className="plan-type-header">Socrative PRO</span>
      </div>,
      LicensePurchase({ model, controller, license }),
    ];
  }

  if (license.licenseStep === Constants.PAYMENT_STEP) {
    let CreditCardIcon = GenericCardIcon;

    if (license.cardType === Constants.AMERICAN_EXPRESS) {
      CreditCardIcon = AmexIcon;
    } else if (license.cardType === Constants.MASTERCARD) {
      CreditCardIcon = MasterCardIcon;
    } else if (license.cardType === Constants.VISA) {
      CreditCardIcon = VisaIcon;
    }

    let i = 1;
    const monthChildren = [];

    while (i <= 12) {
      let displayMonth = i.toString();

      if (/^\d$/.test(displayMonth)) {
        displayMonth = '0' + displayMonth;
      }

      monthChildren.push({
        title: displayMonth,
        value: displayMonth,
        selected: displayMonth === license.month,
      });

      i++;
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const yearChildren = [];

    for (i = 0; i <= 10; i++) {
      yearChildren.push({
        title: currentYear + i,
        value: currentYear + i,
        selected: currentYear + i === license.year,
      });
    }

    const paymentInformationChildren = [
      <span
        key="paymentInformationChildren-span-1"
        className="payment-information-header-text"
      >
        {translate('Payment Information', true)}
      </span>,
    ];

    children = [
      <div key="children-div-7" className="order-summary-header">
        <span className="order-summary-header-text">
          {translate('Order Summary', true)}
        </span>
        <span
          className="order-summary-right-column link"
          onClick={() => {
            if (!license.makingPurchase) {
              controller.previousClicked();
            }
          }}
        >
          {translate('Change')}
        </span>
      </div>,
      <div
        key="children-div-8"
        className="order-summary-row first-order-summary-row"
      >
        <span className="license-receipt-left-column">
          {translate('Price Per Seat')}
        </span>
        <span className="order-summary-right-column">
          {license.pricePerSeatFullFormatted}
        </span>
      </div>,
      showDiscountRow(),
      <div key="children-div-10" className="order-summary-row">
        <span className="license-receipt-left-column">
          {translate('Seats')}
        </span>
        <span className="order-summary-right-column">{license.seats}</span>
      </div>,
      <div key="children-div-11" className="order-summary-row">
        <span className="license-receipt-left-column">
          {translate('Years')}
        </span>
        <span className="order-summary-right-column">{license.years}</span>
      </div>,
      <div key="children-div-12" className="order-summary-row">
        <span className="license-receipt-left-column">
          {translate('Renewal Date')}
        </span>
        <span className="order-summary-right-column">
          {license.renewalDateFormatted}
        </span>
      </div>,
      <div key="children-div-13" className="order-summary-total">
        <span className="order-summary-total-column">
          {translate('Total', true)}
        </span>
        <span className="order-summary-total-column order-summary-right-column">
          {`${license.totalPriceFormatted} USD`}
        </span>
      </div>,
      <div key="children-div-14" className="payment-information-header">
        {paymentInformationChildren}
      </div>,
      <div key="children-div-15" className="name-on-card-container">
        <Input
          autoFocus={true}
          containerClass="name-on-card-input-container"
          disabled={license.makingPurchase}
          id="nameOnCard"
          label={translate('Name On Card')}
          maxLength={255}
          onChange={(event: { target: { id: any; value: any } }) =>
            controller.inputChanged(event.target.id, event.target.value)
          }
          status={license.errors.nameOnCard}
          value={license.nameOnCard}
        />
      </div>,
      <div key="children-div-16" className="card-details">
        <div className="card-detail-container">
          <Input
            containerClass="card-number-input-container"
            disabled={license.makingPurchase}
            id="cardNumber"
            label={translate('Card Number')}
            maxLength={255}
            onChange={(event: { target: { id: any; value: any } }) =>
              controller.inputChanged(event.target.id, event.target.value)
            }
            status={license.errors.cardNumber}
            value={license.cardNumber}
          />
          <CreditCardIcon className="credit-card-icon" />
        </div>
        <div className="card-detail-container card-month-container">
          <p className="">{translate('Expiration Date')}</p>
          <Select
            disabled={license.makingPurchase}
            error={license.errors.month.error}
            label={translate('Month')}
            onSelect={(name: any, value: any) => controller.monthChanged(value)}
          >
            {monthChildren}
          </Select>
        </div>
        <div className="card-detail-container card-year-container">
          <p
            className=""
            // Hack to help Firefox keep things lined up.
            dangerouslySetInnerHTML={{ __html: '&nbsp;' }}
          />
          <Select
            disabled={license.makingPurchase}
            error={license.errors.year.error}
            label={translate('Year')}
            onSelect={(name: any, value: any) => controller.yearChanged(value)}
          >
            {yearChildren}
          </Select>
        </div>
        <div className="card-detail-container card-detail-container-cvc">
          <Input
            containerClass="card-cvc-input-container"
            disabled={license.makingPurchase}
            id="cvc"
            label={translate('CVC')}
            maxLength={10}
            onChange={(event: { target: { id: any; value: any } }) =>
              controller.inputChanged(event.target.id, event.target.value)
            }
            status={license.errors.cvc}
            value={license.cvc}
          />
        </div>
      </div>,
      <span key="children-span-2" className="license-renewal-blurb">
        {translate(
          'Your subscription will automatically renew on {0}. We will send you a reminder email 30 days prior to renewal. You may disable auto-renew at any time via your account settings.'
        ).format(license.renewalDateFormatted)}
      </span>,
      <div key="children-div-17" className="license-buttons-container">
        <button
          className="license-previous-button action-button action-button-200 secondary-action-button"
          disabled={license.makingPurchase}
          id="licensePreviousButton"
          onClick={controller.previousClicked}
        >
          {translate('Previous', true)}
        </button>
        <button
          className="license-purchase-button action-button action-button-200"
          disabled={license.makingPurchase}
          id="licensePurchaseButton"
          onClick={controller.purchaseClicked}
        >
          {translate('Purchase', true)}
        </button>
      </div>,
    ];
  }

  if (license.licenseStep === Constants.COMPLETE_STEP) {
    children = [
      <div key="children-div-18" className="payment-receipt-container">
        <div className="payment-complete-container">
          <SuccessIconLarge className="payment-complete-icon" />
          <span className="payment-complete-header">
            {translate('Payment Complete!')}
          </span>
        </div>
        <div
          className={
            'my-account-button-container' +
            (props.registration ? '' : ' old-hidden')
          }
        >
          <button
            className="action-button action-button-200"
            onClick={controller.myAccountClicked}
          >
            {translate('My Account', true)}
          </button>
        </div>
        <span className="license-receipt-sent">
          {translate(
            'A copy of this receipt has been sent to your registered email: {0}.'
          ).format(model.email)}
        </span>
        <span className="license-receipt-divider" />
        <span className="account-information-header">
          {translate('Account Information', true)}
        </span>
        <div className="order-summary-row first-order-summary-row">
          <span className="license-receipt-left-column">
            {translate('Name')}
          </span>
          <span className="order-summary-right-column">
            {`${model.lastName}, ${model.firstName}`}
          </span>
        </div>
        <div
          className={
            'order-summary-row' + (model.schoolName ? '' : ' old-hidden')
          }
        >
          <span className="license-receipt-left-column">
            {translate('School')}
          </span>
          <span className="order-summary-right-column">{model.schoolName}</span>
        </div>
        <div className="order-summary-row">
          <span className="license-receipt-left-column">
            {translate('Email')}
          </span>
          <span className="order-summary-right-column">{model.email}</span>
        </div>
        <span className="license-receipt-information-header">
          {translate('License Information', true)}
        </span>
        <div className="order-summary-row first-order-summary-row">
          <span className="license-receipt-left-column">
            {translate('License Key')}
          </span>
          <span className="order-summary-right-column license-key-right-column">
            {license.key}
          </span>
        </div>
        <div className="order-summary-row">
          <span className="license-receipt-left-column">
            {translate('Number of Seats')}
          </span>
          <span className="order-summary-right-column">{license.seats}</span>
        </div>
        <div className="order-summary-row">
          <span className="license-receipt-left-column">
            {translate('Number of Years')}
          </span>
          <span className="order-summary-right-column">{license.years}</span>
        </div>
        <div className="order-summary-row">
          <span className="license-receipt-left-column">
            {translate('Renewal Date')}
          </span>
          <span className="order-summary-right-column">
            {license.renewalDateFormatted}
          </span>
        </div>
        <span className="license-receipt-information-header">
          {translate('Payment Information')}
        </span>
        <div className="order-summary-row first-order-summary-row">
          <span className="license-receipt-left-column">
            {translate('Order Number')}
          </span>
          <span className="order-summary-right-column">{`SOC${license.id}`}</span>
        </div>
        <div className="order-summary-row">
          <span className="license-receipt-left-column">
            {translate('Payment Method')}
          </span>
          <span className="order-summary-right-column">
            {`Credit Card (**** ${license.cardNumber.substr(-4, 4)})`}
          </span>
        </div>
        <div className="order-summary-row">
          <span className="license-receipt-left-column">
            {translate('Price Per Seat')}
          </span>
          <span className="order-summary-right-column">
            {license.pricePerSeatFullFormatted}
          </span>
        </div>
        {showDiscountRow()}
        <div className="order-summary-row">
          <span className="license-receipt-left-column license-receipt-total">
            {translate('Order Total')}
          </span>
          <span className="order-summary-right-column">
            {license.totalPriceFormatted}
          </span>
        </div>
        <span className="license-receipt-divider" />
      </div>,
    ];
  }

  return (
    <>
      <div className="purchase-license-container">{children}</div>
    </>
  );
}
